'use client'

import * as React from "react"
import Link from "next/link"
import { usePostHog } from 'posthog-js/react'
import { cn } from "@/lib/utils"
import { enhanceAppHypertaskUrl } from "@/lib/redirect-utils"

interface InternalLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children: React.ReactNode
  href: string
  external?: boolean
  openInNewTab?: boolean
  className?: string
}

// Client-side utility to add PostHog session ID and UTM params for app.hypertask.ai URLs
function enhanceAppUrl(href: string, posthogInstance?: { get_session_id?: () => string | undefined | null }): string {
  try {
    // First enhance with PostHog session ID using centralized utility
    // This already includes UTM parameters from cookies, so we can just return it
    return enhanceAppHypertaskUrl(href, posthogInstance)
  } catch (error) {
    console.warn("Failed to enhance app URL:", error);
    return href
  }
}

export const InternalLink = React.forwardRef<HTMLAnchorElement, InternalLinkProps>(
  ({ children, className, href, external, openInNewTab, ...props }, ref) => {
    const posthog = usePostHog()
    const [enhancedHref, setEnhancedHref] = React.useState(href)

    React.useEffect(() => {
      // Enhance any app.hypertask.ai URLs with PostHog session ID and UTM params
      const enhanced = enhanceAppUrl(href, posthog)
      setEnhancedHref(enhanced)
    }, [href, posthog])

    if (external || href.startsWith('http') || openInNewTab) {
      return (
          <a
            href={enhancedHref}
            // target="_blank"
            // rel="noopener noreferrer"
            className={cn(className, "")}
            ref={ref}
            {...props}
          >
            {children}
          </a>
      )
    }

    return (
      <Link href={enhancedHref} className={className} ref={ref} {...props}>
        {children}
      </Link>
    )
  }
)

InternalLink.displayName = "InternalLink"
